import Button from "~/components/ui/Button"
import { STATUSES } from "~/constants"
import ArrowLeftOnRectangleIconSolid from "../../../../../../assets/images/park__icon_ux__check_out.svg"
import {
  CheckBadgeIcon as CheckBadgeIconSolid,
  EllipsisHorizontalIcon,
  PrinterIcon,
} from "@heroicons/react/24/solid"
import { useSummary } from "./useSummary"
import SitesDropdown from "~/components/Reservations/SitesDropdown/SitesDropdown"
import classNames from "~/utils/classNames"
import { isInclusivelyBeforeDay, SingleDatePicker } from "react-dates"
import parseDateTime from "~/utils/parseDateTime"
import moment, { Moment } from "moment"
import BookingIntervalsDropdown from "../../BookingIntervalsDropdown"
import { DateTime } from "luxon"
import InputFieldWithLabel from "../../../Inputs/InputFieldWithLabel"
import RichTextInput from "~/components/ui/RichTextInput"
import { Listbox, Menu, Transition } from "@headlessui/react"
import { Fragment } from "react/jsx-runtime"
import { SetStateAction, useEffect, useState } from "react"
import { PencilIcon } from "@heroicons/react/20/solid"
import DropdownButton, { DropdownOption } from "~/components/ui/Admin/DropdownButton"

type Props = {
  isLoading: boolean
  setIsLoading: (signal: boolean) => void
  handlePrint: any
  startDate: Moment
  endDate: Moment
  setStartDate: (v: Moment | null) => void
  setEndDate: (v: Moment | null) => void
  isProcessing: boolean
  passengers: React.MutableRefObject<undefined>
  kid_campers: React.MutableRefObject<undefined>
  pets_info: React.MutableRefObject<undefined>
  tos_agreed_at?: string
  isTermsAgreed: boolean
  setIsSignModalOpen: React.Dispatch<SetStateAction<boolean>>
}

export const Summary: React.FC<Props> = (props) => {
  const {
    handleClickCheckedIn,
    isMutatingUpdateReservation,
    siteId,
    setSiteId,
    campingStyle,
    isHourly,
    camp,
    startDateValue,
    endDateValue,
    selectedReservation,
    selectedBookingIntervalId,
    setSelectedBookingIntervalId,
    additionalNotesValue,
    setAdditionalNotesValue,
    isFocusStart,
    setIsFocusStart,
    isFocusEnd,
    setIsFocusEnd,
  } = useSummary({
    setIsLoading: props.setIsLoading,
    startDate: props.startDate,
    endDate: props.endDate,
  })

  return (
    <>
      <div className="flex items-center justify-between mb-0.5">
        <div className="font-bold text-xl">Summary</div>
        {selectedReservation?.status !== STATUSES.BLOCKED && (
          <div className="flex gap-2">
            {camp.tos_requires_signature && (
              <div className="hidden custom-500:block">
                <Button
                  type="button"
                  variant="white"
                  size="small"
                  shadow="small"
                  className={"items-center flex text-gray-600 h-full"}
                  onClick={() => props.setIsSignModalOpen(true)}
                >
                  <PencilIcon className="w-4.5 h-4.5 mr-2" />
                  {props.isTermsAgreed ? "View e-signature" : "Take e-signature"}
                </Button>
              </div>
            )}

            <div className="hidden custom-500:block">
              <Button
                type="button"
                variant="white"
                size="small"
                shadow="small"
                className={"flex items-center text-gray-600 h-full"}
                disabled={isMutatingUpdateReservation}
                onClick={handleClickCheckedIn}
              >
                {selectedReservation?.is_checked_in ? (
                  !selectedReservation?.is_checked_out && (
                    <img src={ArrowLeftOnRectangleIconSolid} className="w-5 h-5 mr-2 text-black" />
                  )
                ) : (
                  <CheckBadgeIconSolid className="w-5 h-5 mr-2" style={{ color: "#3A8860" }} />
                )}
                {selectedReservation?.is_checked_in
                  ? !selectedReservation?.is_checked_out
                    ? "Check-out"
                    : "Remove checked status"
                  : "Check-in"}
              </Button>
            </div>
            {selectedReservation?.status !== STATUSES.IMPORTED && (
              <div className="hidden custom-500:block">
                <Button
                  type="button"
                  variant="white"
                  size="small"
                  shadow="small"
                  className="flex-shrink-0 justify-self-end flex items-center text-gray-600 h-full"
                  onClick={props.handlePrint}
                  children={
                    <>
                      <PrinterIcon className="w-5 h-5 mr-2" />
                      Print
                    </>
                  }
                />
              </div>
            )}
            <DropdownButton
              options={
                [
                  camp.tos_requires_signature && {
                    label: (
                      <div className="flex items-center">
                        <PencilIcon className="w-5 h-5 mr-2" />
                        {props.isTermsAgreed ? "View e-signature" : "Take e-signature"}
                      </div>
                    ),
                    onClick: (e) => props.setIsSignModalOpen(true),
                  },
                  {
                    label: (
                      <div className="flex items-center">
                        {selectedReservation?.is_checked_in ? (
                          !selectedReservation?.is_checked_out && (
                            <img src={ArrowLeftOnRectangleIconSolid} className="w-5 h-5 mr-2 text-black" />
                          )
                        ) : (
                          <CheckBadgeIconSolid className="w-5 h-5 mr-2" style={{ color: "#3A8860" }} />
                        )}
                        {selectedReservation?.is_checked_in
                          ? !selectedReservation?.is_checked_out
                            ? "Check-out"
                            : "Remove checked status"
                          : "Check-in"}
                      </div>
                    ),
                    onClick: handleClickCheckedIn,
                  },
                  selectedReservation?.status !== STATUSES.IMPORTED && {
                    label: (
                      <div className="flex items-center">
                        <PrinterIcon className="w-5 h-5 mr-2" />
                        Print receipt
                      </div>
                    ),
                    onClick: props.handlePrint,
                  },
                ].filter(Boolean) as DropdownOption[]
              }
            >
              <Button
                variant="white"
                className="flex items-center text-gray-600"
                size="small"
                shadow="small"
                type="button"
              >
                <EllipsisHorizontalIcon className="h-6 w-6 text-gray-600" aria-hidden="true" />
              </Button>
            </DropdownButton>
          </div>
        )}
      </div>
      <div className="">
        <div className="w-full mb-3">
          <SitesDropdown
            label="Site"
            disabled={selectedReservation?.deleted_at !== null}
            reservation={selectedReservation}
            selectedReservationType={selectedReservation?.kind}
            selectedIds={[siteId]}
            setSelectedIds={(ids: string[]) => setSiteId(ids[0])}
            fetchSitesAvailability
            startDateString={moment(startDateValue).toISOString()}
            endDateString={moment(endDateValue).toISOString()}
            isSiteLocked={selectedReservation?.is_locked}
          />
        </div>
        <input hidden id="site_id" name="site_id" value={siteId} />
      </div>
      {campingStyle?.booking_intervals?.length || 0 > 0 ? (
        <>
          <div className="flex mb-2 gap-2">
            <div className="flex-1 grow">
              <div>
                <label htmlFor="name" className="block font-semibold text-md text-gray-900">
                  Check-in
                </label>
                <div className={classNames("datepicker-disabled-styles", isHourly ? "flex items-center" : "block")}>
                  <SingleDatePicker
                    noBorder
                    id="start_date"
                    block
                    date={parseDateTime(moment(startDateValue)?.toISOString(), camp.timezone, { asMoment: true })}
                    onDateChange={(date) => {
                      if (!date) return

                      props.setStartDate(date)
                      if (props.endDate && date.isSameOrAfter(props.endDate)) {
                        props.setEndDate(null)
                      }
                    }}
                    isOutsideRange={(day) => false}
                    focused={isFocusStart}
                    onFocusChange={(e) => setIsFocusStart(e.focused)}
                    numberOfMonths={1}
                    hideKeyboardShortcutsPanel={true}
                    displayFormat={camp.date_format}
                    disabled={
                      selectedReservation?.status === STATUSES.IMPORTED ||
                      selectedReservation?.status === STATUSES.BLOCKED_IMPORT ||
                      selectedReservation?.deleted_at !== null
                    }
                    readOnly={
                      selectedReservation?.status === STATUSES.IMPORTED ||
                      selectedReservation?.status === STATUSES.BLOCKED_IMPORT ||
                      selectedReservation?.deleted_at !== null
                    }
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 grow">
              <div>
                <label htmlFor="name" className="block font-semibold text-md text-gray-900">
                  Check-out
                </label>
                <div className={classNames("datepicker-disabled-styles", isHourly ? "flex items-center" : "block")}>
                  <SingleDatePicker
                    noBorder
                    id="end_date"
                    block
                    date={parseDateTime(moment(endDateValue)?.toISOString(), camp.timezone, { asMoment: true })}
                    onDateChange={(date) => props.setEndDate(date)}
                    focused={isFocusEnd}
                    onFocusChange={(e) => {
                      setIsFocusEnd(e.focused)
                    }}
                    numberOfMonths={1}
                    initialVisibleMonth={() => props.endDate || props.startDate || moment()}
                    hideKeyboardShortcutsPanel={true}
                    isOutsideRange={(day) => isInclusivelyBeforeDay(day, props.startDate)}
                    displayFormat={camp.date_format}
                    disabled={
                      selectedReservation?.status === STATUSES.IMPORTED ||
                      selectedReservation?.status === STATUSES.BLOCKED_IMPORT ||
                      selectedReservation?.deleted_at !== null
                    }
                    readOnly={
                      selectedReservation?.status === STATUSES.IMPORTED ||
                      selectedReservation?.status === STATUSES.BLOCKED_IMPORT ||
                      selectedReservation?.deleted_at !== null
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex mb-4 gap-2">
            <BookingIntervalsDropdown
              className="w-full flex-1"
              bookingIntervals={campingStyle?.booking_intervals}
              selectedIntervalId={selectedBookingIntervalId}
              onSelectId={(intervalId) => {
                let interval = campingStyle?.booking_intervals.find((bi) => bi.id === intervalId)
                if (interval) {
                  const splitStartTime = interval.start_time.split(":")
                  const splitEndTime = interval.end_time.split(":")
                  const mStartDate = parseDateTime(props.startDate.toISOString(), camp.timezone).set({
                    hour: Number(splitStartTime[0]),
                    minute: Number(splitStartTime[1]),
                  })
                  const mEndDate = DateTime.fromISO(props.endDate?.toISOString(), { zone: camp.timezone }).set({
                    hour: Number(splitEndTime[0]),
                    minute: Number(splitEndTime[1]),
                  })
                  props.setStartDate(moment(mStartDate.toString()))

                  if (mEndDate.isValid) {
                    props.setEndDate(moment(mEndDate.toString()))
                  }
                }
                setSelectedBookingIntervalId(intervalId)
              }}
            />
            <div className="flex flex-1 gap-2">
              <InputFieldWithLabel
                type="time"
                step="900"
                className="flex-grow"
                disabled={
                  selectedReservation?.status === STATUSES.IMPORTED ||
                  selectedReservation?.status === STATUSES.BLOCKED_IMPORT
                }
                value={DateTime.fromISO(moment(startDateValue).toISOString(), {
                  zone: camp.timezone,
                }).toFormat("HH:mm")}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(":")
                  const mStartDate = parseDateTime(props.startDate.toISOString(), camp.timezone).set({
                    hour: Number(hours),
                    minute: Number(minutes),
                  })
                  props.setStartDate(moment(mStartDate.toString()))
                }}
                onBlur={(time) => {
                  // TODO: this logic is correct, but gets overwritten on save. Let's remove that logic to overwrite the times
                  const splitTime = time.target.value.split(":")
                  const mStartDate = parseDateTime(props.startDate.toISOString(), camp.timezone).set({
                    hour: Number(splitTime[0]),
                    minute: Number(splitTime[1]),
                  })
                  props.setStartDate(moment(mStartDate.toString()))
                }}
                dataTestId="start-time"
              />
              <InputFieldWithLabel
                type="time"
                step="900"
                className="flex-grow"
                disabled={
                  selectedReservation?.status === STATUSES.IMPORTED ||
                  selectedReservation?.status === STATUSES.BLOCKED_IMPORT
                }
                value={DateTime.fromISO(moment(endDateValue).toISOString(), {
                  zone: camp.timezone,
                }).toFormat("HH:mm")}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(":")
                  const mEndDate = DateTime.fromISO(props.endDate?.toISOString(), { zone: camp.timezone }).set({
                    hour: Number(hours),
                    minute: Number(minutes),
                  })
                  if (mEndDate.isValid) {
                    props.setEndDate(moment(mEndDate.toString()))
                  }
                }}
                onBlur={(time) => {
                  // TODO: this logic is correct, but gets overwritten on save. Let's remove that logic to overwrite the times
                  const splitTime = time.target.value.split(":")
                  const mEndDate = DateTime.fromISO(props.endDate?.toISOString(), { zone: camp.timezone }).set({
                    hour: Number(splitTime[0]),
                    minute: Number(splitTime[1]),
                  })
                  mEndDate.isValid ? props.setEndDate(moment(mEndDate.toString())) : props.setEndDate(null)
                }}
                dataTestId="end-time"
              />
            </div>
          </div>
        </>
      ) : (
        <div className="flex mb-2">
          <div className="flex-1 grow">
            <div>
              <label htmlFor="name" className="block font-semibold text-md text-gray-900">
                Check-in
              </label>
              {/* <CalendarDaysIcon className='h-7 w-7 text-gray-600 absolute top-1.5 left-2' aria-hidden='true' /> */}
              <div className={classNames("datepicker-disabled-styles", isHourly ? "flex items-center" : "block")}>
                <SingleDatePicker
                  noBorder
                  id="start_date"
                  block
                  date={parseDateTime(moment(startDateValue)?.toISOString(), camp.timezone, { asMoment: true })}
                  onDateChange={(date) => {
                    if (!date) return

                    props.setStartDate(date)
                    if (props.endDate && date.isSameOrAfter(props.endDate)) {
                      props.setEndDate(null)
                    }
                  }}
                  isOutsideRange={(day) => false}
                  focused={isFocusStart}
                  onFocusChange={(e) => setIsFocusStart(e.focused)}
                  numberOfMonths={1}
                  hideKeyboardShortcutsPanel={true}
                  displayFormat={camp.date_format}
                  disabled={
                    selectedReservation?.status === STATUSES.IMPORTED ||
                    selectedReservation?.status === STATUSES.BLOCKED_IMPORT
                  }
                />
                {isHourly && (
                  <InputFieldWithLabel
                    type="time"
                    step="900"
                    className="w-36 ml-2"
                    disabled={
                      selectedReservation?.status === STATUSES.IMPORTED ||
                      selectedReservation?.status === STATUSES.BLOCKED_IMPORT
                    }
                    defaultValue={
                      selectedReservation?.start_date
                        ? DateTime.fromISO(selectedReservation?.start_date, {
                            zone: camp.timezone,
                          }).toFormat("HH:mm")
                        : null
                    }
                    onBlur={(time) => {
                      // TODO: this logic is correct, but gets overwritten on save. Let's remove that logic to overwrite the times
                      const splitTime = time.target.value.split(":")
                      props.setStartDate(
                        parseDateTime(props.startDate?.toISOString(), camp.timezone, { asMoment: true })
                          .hours(Number(splitTime[0]))
                          .minutes(Number(splitTime[1])),
                      )
                    }}
                    dataTestId="start-time"
                  />
                )}
              </div>
            </div>
          </div>
          <div className="ml-4 flex-1 grow">
            <div>
              <label htmlFor="name" className="block font-semibold text-md text-gray-900">
                Check-out
              </label>
              <div className={classNames("datepicker-disabled-styles", isHourly ? "flex items-center" : "block")}>
                <SingleDatePicker
                  noBorder
                  id="end_date"
                  block
                  date={parseDateTime(moment(endDateValue)?.toISOString(), camp.timezone, { asMoment: true })}
                  onDateChange={(date) => props.setEndDate(date)}
                  focused={isFocusEnd}
                  onFocusChange={(e) => {
                    setIsFocusEnd(e.focused)
                  }}
                  numberOfMonths={1}
                  initialVisibleMonth={() => props.endDate || props.startDate || moment()}
                  hideKeyboardShortcutsPanel={true}
                  isOutsideRange={(day) => isInclusivelyBeforeDay(day, props.startDate)}
                  displayFormat={camp.date_format}
                  disabled={
                    selectedReservation?.status === STATUSES.IMPORTED ||
                    selectedReservation?.status === STATUSES.BLOCKED_IMPORT
                  }
                />
                {isHourly && (
                  <InputFieldWithLabel
                    type="time"
                    step="900"
                    className="w-36 ml-2"
                    disabled={
                      selectedReservation?.status === STATUSES.IMPORTED ||
                      selectedReservation?.status === STATUSES.BLOCKED_IMPORT
                    }
                    defaultValue={
                      selectedReservation?.end_date
                        ? DateTime.fromISO(selectedReservation?.end_date, {
                            zone: camp.timezone,
                          }).toFormat("HH:mm")
                        : null
                    }
                    onBlur={(time) => {
                      // TODO: this logic is correct, but gets overwritten on save. Let's remove that logic to overwrite the times
                      const splitTime = time.target.value.split(":")
                      const mEndDate = moment(props.endDate)
                      mEndDate.isValid()
                        ? props.setEndDate(
                            parseDateTime(props.endDate?.toISOString(), camp.timezone, { asMoment: true })
                              .hours(Number(splitTime[0]))
                              .minutes(Number(splitTime[1])),
                          )
                        : props.setEndDate(null)
                    }}
                    dataTestId="end-time"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex mb-2">
        <div className="flex-grow">
          <label htmlFor="name" className="block font-semibold text-md text-gray-900">
            Reservation Notes
          </label>
          <div className="mt-1">
            <input name="notes" id="notes" hidden value={additionalNotesValue} />
            <RichTextInput
              className="break-all"
              defaultValue={selectedReservation?.notes}
              value={additionalNotesValue}
              setValue={setAdditionalNotesValue}
              id="notes"
              dataTestId="notes"
            />
          </div>
        </div>
      </div>
      {selectedReservation?.status != STATUSES.BLOCKED && (
        <div className="flex mb-12">
          <InputFieldWithLabel
            type="number"
            full
            inputRef={props.passengers}
            name="passengers"
            label="Adults"
            defaultValue={selectedReservation?.passengers}
            disabled={props.isProcessing}
            dataTestId="passengers"
          />
          <InputFieldWithLabel
            type="number"
            full
            inputRef={props.kid_campers}
            name="kid_campers"
            label="Kids"
            defaultValue={selectedReservation?.kid_campers}
            disabled={props.isProcessing}
            className="ml-4"
            dataTestId="kid-campers"
          />
          <InputFieldWithLabel
            type="text"
            full
            inputRef={props.pets_info}
            name="pets_info"
            label="Pets"
            defaultValue={selectedReservation?.pets_info}
            disabled={props.isProcessing}
            className="ml-4"
            dataTestId="pets-info"
          />
        </div>
      )}
    </>
  )
}
